import React, { useEffect, useState } from 'react';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Button from '../../components/elements/Button';
import Toast from '../../components/forms/Toast';
import { useTranslation } from 'react-i18next';
import { apiRequest, fetchUserInfo } from '../../utils';

const defaultCriteria = {
  criteria1: 0,
  criteria2: 0,
  criteria3: 0,
};

const criteriaProfiles = {
  0: {
    labels: ['Stability', 'Dimensions', 'Team energy'],
    max: [10, 10, 10],
    total: 30,
  },
  1: {
    labels: ['Smooth rolling', 'Structural coherence', 'Team energy'],
    max: [20, 20, 20],
    total: 60,
  },
  2: {
    labels: ['Creativity', 'Structure integrity', 'Team energy'],
    max: [30, 30, 20],
    total: 80,
  },
};

const fallbackProfile = {
  labels: ['Criteria 1', 'Criteria 2', 'Criteria 3'],
  max: [5, 5, 5],
  total: 15,
};

const getProfile = (index) => criteriaProfiles[index] || fallbackProfile;

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    fetchUserInfo('/crew-score-team', setUserInfo, setLoading, setError);
  }, []);

  useEffect(() => {
    if (!userInfo?.scores) return;

    const initialState = {};
    userInfo.scores.forEach((score, index) => {
      const profile = getProfile(index);
      initialState[score.id] = {
        criteria1: Math.min(Number(score.score_1) || defaultCriteria.criteria1, profile.max[0]),
        criteria2: Math.min(Number(score.score_2) || defaultCriteria.criteria2, profile.max[1]),
        criteria3: Math.min(Number(score.time_1) || defaultCriteria.criteria3, profile.max[2]),
      };
    });
    setRatings(initialState);
  }, [userInfo]);

  const handleSliderChange = (scoreId, field, value) => {
    setRatings((prev) => ({
      ...prev,
      [scoreId]: {
        ...(prev[scoreId] || defaultCriteria),
        [field]: Number(value),
      },
    }));
  };

  const submitScore = async (scoreId) => {
    try {
      const selected = ratings[scoreId] || defaultCriteria;
      const response = await apiRequest('POST', '/crew-score-team', {
        score_id: scoreId,
        criteria1: selected.criteria1,
        criteria2: selected.criteria2,
        criteria3: selected.criteria3,
      });

      setError(null);
      setSuccessMessage(response.message || 'ratingSaved');
      setShowToast(true);
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (err) {
      setSuccessMessage(null);
      setError(err.message);
      setShowToast(true);
    }
  };

  return (
    <Screen>
        <div className="top">
      <Card>
        <Back to="/crew-teams" />
        <Title my="1" className="mt-3">{t('scoreATeam')}</Title>
      </Card>

      {userInfo?.scores?.length > 0 ? (
        userInfo.scores.map((score, index) => {
          const profile = getProfile(index);
          const selected = ratings[score.id] || defaultCriteria;
          return (
            <div className="dropdown px-3" key={score.id}>
              <Button
                p="1"
                mb="1"
                theme="light"
                dataBsToggle="dropdown"
                ariaExpanded={false}
              >
                <div className="row">
                  <div className="col-2 d-flex align-items-center">
                    <Image
                      url={score.photo_url ? score.photo_url : require('./../../assets/images/fallback/activity.png')}
                      size="3rem"
                      className="circle mb-1"
                      reload={true}
                    />
                  </div>
                  <div className="col-9 d-flex align-items-center text-start">
                    <div>
                      <b>{score.name}</b>
                      <span className="badge text-bg-primary ms-2">
                        Points: {score.score || 0}
                      </span>
                    </div>
                  </div>
                  <div className="col-1 d-flex align-items-center">
                    <Icon
                      name="menu-dots-vertical"
                      theme="secondary"
                      style={{ marginLeft: '-0.8rem' }}
                    />
                  </div>
                </div>
              </Button>

              <div className="dropdown-menu dropdown-menu-end p-3" style={{ minWidth: '320px' }}>
                <div className="mb-2">
                  <label className="form-label mb-1">
                    {profile.labels[0]}: <b>{selected.criteria1}</b> / {profile.max[0]}
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max={profile.max[0]}
                    step="1"
                    value={selected.criteria1}
                    onChange={(e) => handleSliderChange(score.id, 'criteria1', e.target.value)}
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label mb-1">
                    {profile.labels[1]}: <b>{selected.criteria2}</b> / {profile.max[1]}
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max={profile.max[1]}
                    step="1"
                    value={selected.criteria2}
                    onChange={(e) => handleSliderChange(score.id, 'criteria2', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label mb-1">
                    {profile.labels[2]}: <b>{selected.criteria3}</b> / {profile.max[2]}
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max={profile.max[2]}
                    step="1"
                    value={selected.criteria3}
                    onChange={(e) => handleSliderChange(score.id, 'criteria3', e.target.value)}
                  />
                </div>

                <div className="small text-secondary mb-3">
                  Max total: {profile.total}
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100 border-0"
                  onClick={() => submitScore(score.id)}
                >
                  {t('save')}
                </button>
              </div>
            </div>
          );
        })
      ) : (
        !loading && (
          <Card>
            <Title heading="6">No scores found</Title>
          </Card>
        )
      )}

      <Toast showToast={showToast} setShowToast={setShowToast} message={t(successMessage || error)} />
      </div>
    </Screen>
  );
};

export default Page;
